import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, ChevronDown, BookOpen } from 'lucide-react';
import syncService from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './shared/Spinner';
import ReportActions, { ID_LIST_OPEN_LIMIT, htmlCollapsible } from './shared/ReportActions';
import { classifyOnsite, fillSystems, summariseGroupValues, ONSITE_STATUSES, ONSITE_STATUSES_ERRORS_ONLY } from './shared/onsiteInstructions';
import ReportAnnotation, { useReportAnnotations } from './shared/ReportAnnotation';
// STYLE.md: all dates render yyyy-mm-dd, from the shared helper — never an inline toLocaleDateString.
import { formatDate as fmtDate } from '../utils/datetime';
import './OnsiteInstructionsPage.css';

// Every record matching a filter — the whole task, not its newest 1,000 (see searchAllReconciliationRecords).
const fetchAll = (syncId, taskId, filters) => syncService.searchAllReconciliationRecords(syncId, taskId, filters);

// A labelled system chip (logo + name) for the source → target banner.
function SystemChip({ name, image, role }) {
    return (
        <span className="onsite-syschip">
            <span className="onsite-syschip-role">{role}</span>
            <span className="onsite-syschip-main">
                {image ? <img className="onsite-syschip-img" src={image} alt="" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
                <span className="onsite-syschip-name">{name}</span>
            </span>
        </span>
    );
}

// ONE COMPONENT, TWO REPORTS. `errorsOnly` narrows which records are pulled and retitles the page;
// everything else — the classifiers, the grouping, the annotations, the email and CSV — is identical
// and must stay identical. A second page copied from this one would drift on the classifier list, which
// is the part that actually tells the property what to do.
export default function OnsiteInstructionsPage({ errorsOnly = false }) {
    const statuses = errorsOnly ? ONSITE_STATUSES_ERRORS_ONLY : ONSITE_STATUSES;
    const reportTitle = errorsOnly ? 'On-site Instructions (errors only)' : 'On-site Instructions';
    const params = useParams();
    const syncId = params.syncId || params.syncId;
    // Same tolerance as the project id on the line above: the routes address a task by its id now,
    // and older links still carry the uuid.
    const taskId = params.taskId || params.taskId;
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [projectName, setProjectName] = useState('');
    // Server-side facts this screen cannot work out in the browser: how long each group has been
    // outstanding (V460) and any KB entry that explains it. Keyed `groupKey<::>code`, merged onto the
    // locally-classified groups below — the classifier is the same code on both sides, so the keys match.
    const [serverFacts, setServerFacts] = useState({});
    const [syncTemplateId, setSyncTemplateId] = useState(null);
    const [systems, setSystems] = useState({ sourceName: '', sourceImage: '', targetName: '', targetImage: '' });
    const [generatedAt, setGeneratedAt] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!isSuperAdmin) { setLoading(false); return; }
            try {
                setLoading(true); setError(null);
                try {
                    const proj = await syncService.getSyncProjectDetails(syncId);
                    const pd = proj?.projectDetails || proj || {};
                    if (!cancelled) {
                        setProjectName(pd.projectName || proj?.projectName || proj?.name || '');
                        setSystems({
                            sourceName: pd.sourceSystemName || '', sourceImage: pd.sourceSystemImage || '',
                            targetName: pd.targetSystemName || '', targetImage: pd.targetSystemImage || '',
                        });
                    }
                } catch (_) { /* name best-effort */ }

                // On-site items live on ERROR records, plus PROCESSED records that succeeded and still
                // left something to do at the property, plus ANY record a superadmin manually stamped
                // with a site instruction.
                //
                // The PROCESSED ones are fetched from PROCESSED_PROBES rather than a hardcoded phrase:
                // this list was one entry long and adding a classifier without adding its probe meant
                // the classifier never ran, with nothing on the screen to say so.
                // ERROR (did not go through) and WARNING (went through, something still owed at the
                // property). PROCESSED is deliberately absent: it means there is nothing left to do, so a
                // record on this report that is PROCESSED is a record whose status is wrong — fixed where
                // it is stamped, not compensated for here by pulling PROCESSED records back with text
                // probes, which is how a whole page of successful reservations ended up on this report.
                const byStatus = (await Promise.all(
                    statuses.map(st => fetchAll(syncId, taskId, { recordStatus: st }))
                )).flat();
                // A manual instruction is pulled regardless of how the record was classified, but NOT
                // regardless of its status — on the errors-only report a hand-written note on a WARNING
                // record is still a warning, and letting it through would put back exactly what the
                // report exists to leave out.
                const manual = (await fetchAll(syncId, taskId, {}))
                    .filter(r => (r.onsiteInstruction || '').trim())
                    .filter(r => statuses.includes(r.recordStatus));
                const seen = new Set(byStatus.map(r => r.sprRecordUuid));
                const all = [...byStatus];
                for (const r of manual) if (!seen.has(r.sprRecordUuid)) { seen.add(r.sprRecordUuid); all.push(r); }

                const onsite = [];
                for (const r of all) {
                    // A manual instruction wins over the auto-classifier — it is the specific ask for
                    // this record. Records sharing the same text merge into one group.
                    const manualText = (r.onsiteInstruction || '').trim();
                    if (manualText) {
                        onsite.push({ ...r, _g: { key: `manual:${manualText}`, label: 'Site instruction', code: null, system: null, instruction: manualText, manual: true } });
                        continue;
                    }
                    const g = classifyOnsite(r);
                    if (g) onsite.push({ ...r, _g: g });
                }
                if (!cancelled) { setRecords(onsite); setGeneratedAt(new Date()); }

                // Best-effort and deliberately last: the instructions are the report, and a slow or
                // failing lookup must not delay or break them. The screen simply shows no age and no KB.
                try {
                    const rep = await syncService.getProjectOnsiteReport(syncId, errorsOnly, taskId);
                    const facts = {};
                    for (const sc of (rep?.scopes || [])) {
                        for (const g of (sc.groups || [])) {
                            facts[`${g.meta.key}<::>${g.meta.code == null ? '' : g.meta.code}`] = {
                                daysOpen: g.daysOpen, firstSeenAt: g.firstSeenAt,
                                firstNotifiedAt: g.firstNotifiedAt, kb: g.kb || null,
                                kbSignature: g.kbSignature || null,
                            };
                        }
                    }
                    if (!cancelled) { setServerFacts(facts); setSyncTemplateId(rep?.syncTemplateId || null); }
                } catch (_) { /* age + KB are an enhancement, not the report */ }
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to build on-site instructions');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [syncId, taskId, isSuperAdmin, errorsOnly]);

    // Group: scope -> category -> records.
    // Per-section notes, from the same store the Sync Report writes to (V368) — a note added on either
    // report is the same note, keyed on the same group.
    const [annotations, applyAnnotation] = useReportAnnotations(syncId, taskId);

    // Which id lists are expanded. A group of 699 reservation ids buries the next instruction under a
    // screen and a half of numbers, and the ids are for working THROUGH one group — not for finding out
    // what the groups are. Large ones start closed; small ones are shorter than their own heading and
    // hiding them would only add a click.
    const [openIds, setOpenIds] = useState({});
    const idsOpen = (key, count) => (key in openIds ? openIds[key] : count <= ID_LIST_OPEN_LIMIT);
    const toggleIds = (key, count) => setOpenIds(prev => ({ ...prev, [key]: !(key in prev ? prev[key] : count <= ID_LIST_OPEN_LIMIT) }));

    const scopes = useMemo(() => {
        const byScope = {};
        for (const r of records) {
            const scope = r.scopeType || r.scopetype || 'RECORDS';
            const key = r._g.key;
            byScope[scope] = byScope[scope] || {};
            byScope[scope][key] = byScope[scope][key] || { meta: r._g, records: [] };
            byScope[scope][key].records.push(r);
        }
        return Object.entries(byScope).map(([scope, groups]) => ({
            scope,
            // What the records in each group actually name. The same summary the server builds for the
            // emailed and printed report, from the same helper, so the screen and the document agree.
            groups: Object.values(groups)
                .map(g => summariseGroupValues(g, r => r._g && (r._g.detail || r._g.code)))
                .sort((a, b) => b.records.length - a.records.length),
            count: Object.values(groups).reduce((n, g) => n + g.records.length, 0),
        })).sort((a, b) => b.count - a.count);
    }, [records]);

    const safeName = (projectName || 'sync').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();

    // A plain-text rendering of the instructions, reused for CSV and the email body.
    const buildCsv = () => {
        const rows = [['scope', 'category', 'value', 'fixInSystem', 'instruction', 'note', 'identifier', 'sourceId', 'targetId']];
        for (const s of scopes) for (const g of s.groups) for (const r of g.records) {
            rows.push([s.scope, g.meta.label, (r._g && (r._g.detail || r._g.code)) || '',
                sysName(g.meta.system), fill(g.meta.instruction),
                annotations[`onsite:${s.scope}:${g.meta.key}`] || '',
                g.meta.code || '', r.sourcerecordid || '', r.targetRecordId || '']);
        }
        return rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    };
    // Manual site instructions have no fix-in system (which === null) — render no badge for them.
    const sysName = (which) => !which ? '' : which === 'source' ? (systems.sourceName || 'the source system') : (systems.targetName || 'the target system');
    const fill = (text) => fillSystems(text, systems.sourceName, systems.targetName);

    const buildTextBody = () => {
        const lines = [`On-site fixes required — ${projectName || 'Sync project'}`];
        if (systems.sourceName || systems.targetName) lines.push(`Systems: ${systems.sourceName || 'Source'}  ->  ${systems.targetName || 'Target'}`);
        lines.push('');
        for (const s of scopes) {
            lines.push(`## ${s.scope} (${s.count})`);
            for (const g of s.groups) {
                lines.push(`- ${g.meta.label}${g.meta.code ? ` [${g.meta.code}]` : ''} — ${g.records.length} record(s)`);
                if (g.meta.system) lines.push(`  Fix in: ${sysName(g.meta.system)}`);
                lines.push(`  Action: ${fill(g.meta.instruction)}`);
                const note = annotations[`onsite:${s.scope}:${g.meta.key}`];
                if (note) lines.push(`  Note: ${note}`);
                lines.push(`  Records: ${g.records.map(r => `${r.sourcerecordid || '?'}${r.targetRecordId ? `→${r.targetRecordId}` : ''}`).join(', ')}`);
            }
            lines.push('');
        }
        return lines.join('\n');
    };

    // A styled HTML rendering of the instructions for the email body (inline styles only — email
    // clients strip <style>). Mirrors the on-screen layout: systems banner, per-scope sections,
    // groups with a code chip + "Fix in" badge, and a Source→Target table.
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const buildHtmlBody = (intro) => {
        const chip = (bg, bd, fg, txt) => `<span style="font-family:monospace;font-weight:700;color:${fg};background:${bg};border:1px solid ${bd};border-radius:5px;padding:2px 8px;margin-left:6px">${esc(txt)}</span>`;
        const sysBox = (role, name) => `<td style="border:1px solid #e5e7eb;border-radius:8px;padding:8px 14px;background:#f9fafb"><div style="font-size:11px;text-transform:uppercase;color:#9ca3af;font-weight:700;letter-spacing:.04em">${role}</div><div style="font-weight:700;color:#111827;font-size:15px">${esc(name)}</div></td>`;
        const banner = (systems.sourceName || systems.targetName)
            ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 0 18px"><tr>${sysBox('Source', systems.sourceName || 'Source')}<td style="padding:0 12px;font-size:22px;color:#9ca3af;font-weight:700">&rarr;</td>${sysBox('Target', systems.targetName || 'Target')}</tr></table>`
            : '';
        let sections = '';
        for (const s of scopes) {
            let groups = '';
            for (const g of s.groups) {
                const codeChip = g.meta.code ? chip('#ffedd5', '#fdba74', '#7c2d12', g.meta.code) : '';
                const fixChip = g.meta.system ? `<span style="font-size:12px;color:#374151;background:#fff;border:1px solid #e5e7eb;border-radius:999px;padding:2px 9px;margin-left:6px">Fix in: <strong>${esc(sysName(g.meta.system))}</strong></span>` : '';
                const rows = g.records.map(r =>
                    `<tr><td style="padding:4px 12px;border-bottom:1px solid #f3f4f6;font-family:monospace">${esc(r.sourcerecordid || '—')}</td><td style="padding:4px 12px;border-bottom:1px solid #f3f4f6;font-family:monospace;color:#9ca3af">${esc(r.targetRecordId || '—')}</td></tr>`).join('');
                // Folded exactly as the screen folds it, at the same threshold — a group of 1000
                // reservations was arriving as 1000 rows of numbers with the next instruction below it.
                const idTable = `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:13px;background:#fff">
                        <tr><th style="text-align:left;padding:4px 12px;border-bottom:2px solid #e5e7eb;font-size:11px;text-transform:uppercase;color:#6b7280">Source ID${systems.sourceName ? ` (${esc(systems.sourceName)})` : ''}</th><th style="text-align:left;padding:4px 12px;border-bottom:2px solid #e5e7eb;font-size:11px;text-transform:uppercase;color:#6b7280">Target ID${systems.targetName ? ` (${esc(systems.targetName)})` : ''}</th></tr>
                        ${rows}
                    </table>`;
                const idBlock = htmlCollapsible(g.records.length,
                    `${g.records.length} record${g.records.length === 1 ? '' : 's'} — show ids`, idTable);
                groups += `<div style="border:1px solid #e5e7eb;border-left:4px solid #d97706;border-radius:8px;padding:12px 14px;margin:0 0 10px;background:#fffbeb">
                    <div style="font-weight:600;color:#111827;font-size:15px">${esc(g.meta.label)}${codeChip}${fixChip}<span style="float:right;font-weight:700">${g.records.length}</span></div>
                    <p style="font-size:14px;color:#374151;margin:8px 0 10px"><strong>Action:</strong> ${esc(fill(g.meta.instruction))}</p>
                    ${annotations[`onsite:${s.scope}:${g.meta.key}`]
                        ? `<div style="margin:0 0 10px;padding:8px 10px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;font-size:13px;color:#1e3a8a"><strong>Note:</strong> ${esc(annotations[`onsite:${s.scope}:${g.meta.key}`])}</div>`
                        : ''}
                    ${idBlock}</div>`;
            }
            sections += `<h2 style="font-size:16px;color:#111827;border-bottom:1px solid #e5e7eb;padding-bottom:6px;margin:22px 0 10px">${esc(s.scope)} <span style="font-size:12px;color:#6b7280">(${s.count})</span></h2>${groups}`;
        }
        return `<div style="font-family:Arial,Helvetica,sans-serif;color:#111827;max-width:700px;margin:0 auto">
            <h1 style="font-size:20px;margin:0 0 4px">${esc(projectName || 'Sync project')} — On-site Instructions</h1>
            <p style="color:#4b5563;font-size:14px;margin:0 0 14px">Items to correct at the property, then reprocess. Each item shows which system to log into.</p>
            ${banner}
            ${intro ? `<p style="font-size:14px;color:#111827;white-space:pre-wrap;margin:0 0 14px">${esc(intro)}</p>` : ''}
            ${sections}</div>`;
    };

    if (!isSuperAdmin) {
        return <div className="onsite-page"><div className="onsite-denied"><AlertCircle size={20} /><span>Available to superadmins only.</span></div></div>;
    }

    return (
        <div className="onsite-page">
            <div className="onsite-toolbar onsite-no-print">
                <button className="onsite-back" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
                <ReportActions
                    syncId={syncId}
                    taskId={taskId}
                    /* Available on an all-clear too — see ProjectOnsiteReportPage. */
                    disabled={loading || !!error}
                    noun="on-site instructions"
                    defaultSubject={`[${projectName || 'Sync project'}] On-site fixes required${errorsOnly ? ' (errors)' : ''}`}
                    defaultIntro="Please action the on-site fixes listed below, then let us know so we can reprocess the affected records."
                    csvFilename={`${safeName}-onsite-instructions${errorsOnly ? '-errors' : ''}.csv`}
                    buildCsv={buildCsv}
                    buildTextBody={buildTextBody}
                    buildHtmlBody={buildHtmlBody}
                    /* THE EMAIL IS BUILT ON THE SERVER (/opt/core/onsite-render), from the same
                       classifiers and the same annotations this page renders. The body now carries a
                       heading and a count per group and the record ids travel in an attached PDF: a
                       group of 2,000 reservations made an email clients truncated and nobody could
                       read on a phone. buildTextBody/buildHtmlBody stay for Print and as the fallback
                       for any caller that does not opt in. */
                    serverReport={{ report: 'task-onsite', errorsOnly, allowEmpty: records.length === 0 }}
                />
            </div>

            {loading ? (
                <div className="onsite-loading"><Spinner /> Building on-site instructions…</div>
            ) : error ? (
                <div className="onsite-denied"><AlertCircle size={20} /><span>{error}</span></div>
            ) : (
                <div className="onsite-sheet">
                    <header className="onsite-header">
                        <h1 className="onsite-title">{projectName || 'Sync Project'} — {reportTitle}</h1>
                        <p className="onsite-sub">
                            {errorsOnly
                                ? 'Records that did NOT go through and must be corrected at the property, then reprocessed. Warnings are excluded — see the full on-site report for those.'
                                : 'Items that must be corrected at the property, then reprocessed. Each item shows which system to log into.'}
                        </p>
                        {(systems.sourceName || systems.targetName) && (
                            <div className="onsite-systems">
                                <SystemChip name={systems.sourceName || 'Source'} image={systems.sourceImage} role="Source" />
                                <span className="onsite-systems-arrow">→</span>
                                <SystemChip name={systems.targetName || 'Target'} image={systems.targetImage} role="Target" />
                            </div>
                        )}
                        {generatedAt && <div className="onsite-generated">Generated {generatedAt.toLocaleString()}</div>}
                    </header>

                    {scopes.length === 0 ? (
                        <p className="onsite-empty">
                            {errorsOnly
                                ? 'No errors — nothing failed that needs correcting at the property.'
                                : 'No on-site items — nothing needs correcting at the property.'}
                        </p>
                    ) : scopes.map(s => (
                        <section key={s.scope} className="onsite-scope">
                            <h2 className="onsite-scope-title">{s.scope} <span className="onsite-scope-count">{s.count}</span></h2>
                            {s.groups.map((g) => { const f = serverFacts[`${g.meta.key}<::>${g.meta.code == null ? '' : g.meta.code}`] || {}; return (
                                <div key={g.meta.key} className="onsite-group">
                                    <div className="onsite-group-head">
                                        <span className="onsite-group-label">{g.meta.label}</span>
                                        {g.meta.code ? <span className="onsite-code" title="Code to configure / fix">{g.meta.code}</span> : null}
                                        {/* THE VALUES THE RECORDS NAME. The group is one problem with one
                                            instruction, which is right, but it is not one value — the
                                            heading used to quote whichever record was classified first,
                                            so 151 rate-code rejections read as being about the single
                                            rate code of the first one (HIL-90). */}
                                        {g.meta.varies && (g.meta.values || []).length > 1 ? (
                                            <span className="onsite-values" title="Each value, and how many records name it">
                                                {g.meta.values.map(v => (
                                                    <span key={v.value} className="onsite-value">
                                                        {v.value}{v.count > 1 ? <b> ×{v.count}</b> : null}
                                                    </span>
                                                ))}
                                            </span>
                                        ) : null}
                                        {/* HOW LONG IT HAS BEEN OUTSTANDING (V460). Printed, not hidden
                                            behind a hover: "unmapped for 11 days" is the sentence that
                                            makes a property act, and it is the one thing the report
                                            could not say before because it was rebuilt from scratch
                                            every time. Day 0 is left unlabelled — "0 days" on something
                                            first seen this morning reads as a bug. */}
                                        {f.daysOpen > 0 && (
                                            <span
                                                className={`onsite-age${f.daysOpen >= 7 ? ' onsite-age-stale' : ''}`}
                                                title={`First seen ${fmtDate(f.firstSeenAt)}`
                                                    + (f.firstNotifiedAt
                                                        ? ` · property first notified ${fmtDate(f.firstNotifiedAt)}`
                                                        : ' · not yet included in a report sent to the property')}
                                            >
                                                {f.daysOpen} day{f.daysOpen === 1 ? '' : 's'}
                                            </span>
                                        )}
                                        {g.meta.system && (
                                            <span className={`onsite-fixin onsite-fixin-${g.meta.system}`}>
                                                Fix in:
                                                {(g.meta.system === 'source' ? systems.sourceImage : systems.targetImage)
                                                    ? <img className="onsite-fixin-icon" src={g.meta.system === 'source' ? systems.sourceImage : systems.targetImage} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                                                    : null}
                                                <strong>{sysName(g.meta.system)}</strong>
                                            </span>
                                        )}
                                        {/* The count IS the control: it is what a reader looks at to
                                            judge the size of the list, so it is what they reach for to
                                            open it. */}
                                        <button
                                            type="button"
                                            className="onsite-group-count onsite-ids-toggle report-no-print"
                                            onClick={() => toggleIds(g.meta.key, g.records.length)}
                                            aria-expanded={idsOpen(g.meta.key, g.records.length)}
                                            title={idsOpen(g.meta.key, g.records.length)
                                                ? 'Hide the record ids'
                                                : `Show the ${g.records.length} record ids`}
                                        >
                                            {g.records.length}
                                            <ChevronDown
                                                size={13}
                                                className={idsOpen(g.meta.key, g.records.length)
                                                    ? 'onsite-chev onsite-chev-open' : 'onsite-chev'}
                                            />
                                        </button>
                                        {/* Print gets the plain number — a chevron on paper is noise. */}
                                        <span className="onsite-group-count onsite-print-only">{g.records.length}</span>
                                    </div>
                                    <p className="onsite-instruction"><strong>Action:</strong> {fill(g.meta.instruction)}</p>

                                    {/* THE KNOWLEDGE BASE ENTRY FOR THIS ERROR, matched server-side on
                                        the signature of the system's own message (see
                                        /opt/core/error-signature) — so the emailed report carries the
                                        same suggestion as the screen.
                                        PRINTED AS A SUGGESTION, NOT AS THE ACTION. The classifier's
                                        instruction is what we are certain of; a KB entry is what
                                        somebody wrote after seeing this before, and conflating the two
                                        would let a stale note override a rule. */}
                                    {f.kb && (
                                        <div className="onsite-kb">
                                            <div className="onsite-kb-head">
                                                <BookOpen size={13} />
                                                <span className="onsite-kb-title">{f.kb.title}</span>
                                                <span className="onsite-kb-tag">Knowledge base</span>
                                            </div>
                                            <p className="onsite-kb-body">{f.kb.description}</p>
                                        </div>
                                    )}

                                    {/* Offered only where there is no entry yet, and only to a
                                        superadmin: this is how the KB gets filled in, from the screen
                                        where somebody has just worked out what the fix was. Never
                                        printed — it is a control, not report content. */}
                                    {!f.kb && f.kbSignature && isSuperAdmin && (
                                        <button
                                            type="button"
                                            className="onsite-kb-add report-no-print sa-only"
                                            onClick={() => navigate('/admin/knowledge-base?' + new URLSearchParams({
                                                signature: f.kbSignature,
                                                sample: g.meta.sample || g.meta.label || '',
                                                title: g.meta.label || '',
                                                ...(syncTemplateId ? { syncTemplateId: String(syncTemplateId) } : {}),
                                            }))}
                                            title="No knowledge base entry matches this error yet — write one"
                                        >
                                            <BookOpen size={12} /> Add to knowledge base
                                        </button>
                                    )}

                                    {/* The operator's own words on this section. Printed with the
                                        report — it is usually the sentence that tells the property
                                        something the generic instruction cannot. */}
                                    <ReportAnnotation
                                        syncId={syncId}
                                        taskId={taskId}
                                        groupKey={`onsite:${s.scope}:${g.meta.key}`}
                                        value={annotations[`onsite:${s.scope}:${g.meta.key}`]}
                                        onChange={applyAnnotation}
                                        onError={setError}
                                    />
                                    {/* Collapsed with CSS rather than by not rendering: this report is
                                        printed and emailed, and a list that is closed on screen must
                                        still be in the document the property receives. */}
                                    <div className={`onsite-idtable-wrap${idsOpen(g.meta.key, g.records.length) ? '' : ' onsite-collapsed'}`}>
                                        <table className="onsite-idtable">
                                            <thead>
                                                <tr>
                                                    <th>Source ID{systems.sourceName ? ` (${systems.sourceName})` : ''}</th>
                                                    <th>Target ID{systems.targetName ? ` (${systems.targetName})` : ''}</th>
                                                    {/* Only when the group holds more than one value —
                                                        a column repeating the same string on every row
                                                        is noise. */}
                                                    {g.meta.varies ? <th>Value</th> : null}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {g.records.map(r => (
                                                    <tr key={r.sprRecordUuid || r.sourcerecordid}>
                                                        <td>{r.sourcerecordid || '—'}</td>
                                                        <td>{r.targetRecordId || '—'}</td>
                                                        {g.meta.varies
                                                            ? <td>{(r._g && (r._g.detail || r._g.code)) || '—'}</td>
                                                            : null}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ); })}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}
